/**
 * CANONICAL MODULE — app-platform/shared/firstBoot.ts
 *
 * Copied byte-for-byte into every app (`src/lib/firstBoot.ts`). Pure: the key-value store is
 * injected, no MSAL and no app import. Built for W12 (design/W12-storage.md §3.1, P2) on
 * 2026-10-09, promoted from trakr's `draftScope.ts` (step A1, `0508f50`) when contently became the
 * second app to need it.
 *
 * **Fix bugs HERE, then copy outward. Never edit an app's copy in place.**
 *
 * ── What it records ──────────────────────────────────────────────────────────────────────────────
 * P2 of `decideStore`'s ladder is *session continuity*: "the first boot of the W12 build found
 * exactly one MSAL account ALREADY signed in, and no interactive sign-in has happened since"
 * (decision 2, answered 2026-10-08/09). The evidence has to be written down at that first boot,
 * before MSAL handles a redirect and before any sign-in UI can run, because nothing afterwards can
 * reconstruct it. MSAL's account list on its own is never the proof: after "A signs out, B signs
 * in" it holds only B.
 *
 * So each app calls `recordFirstBoot` once per page load, right after `msal.initialize()` and
 * before `handleRedirectPromise()`. Only the first call ever writes; every later boot reads it.
 * `markInteractiveSignIn` is called before every interactive sign-in and ends P2 for good.
 * `firstBootEvidence` turns the record into `OwnershipEvidence['signedInAtFirstBoot']`.
 *
 * Every failure records nobody, which is the safe direction: with no P2 the ladder falls through to
 * P3 (remote overlap) or P4 (hold and ask).
 */

/** The slice of `localStorage` this module needs. */
export interface FirstBootKv {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

export interface FirstBootRecord {
  /** The one MSAL account already signed in when this build first booted, or null. */
  accountId: string | null
  /** Set by any interactive sign-in since. Once true, P2 can never fire on this device. */
  interactiveSince: boolean
  /** ISO time the record was written. */
  at: string
}

function defaultKv(): FirstBootKv | null {
  try {
    return globalThis.localStorage ?? null
  } catch {
    return null
  }
}

/**
 * The stored record, or null when none was written (or it is unreadable).
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export function readFirstBoot(key: string, kv: FirstBootKv | null = defaultKv()): FirstBootRecord | null {
  if (!kv) return null
  try {
    const raw = kv.getItem(key)
    if (raw === null) return null
    const v = JSON.parse(raw) as Partial<FirstBootRecord> | null
    if (typeof v !== 'object' || v === null) return null
    return {
      accountId: typeof v.accountId === 'string' && v.accountId ? v.accountId : null,
      // Anything but an explicit false reads as "an interactive sign-in happened": fail closed.
      interactiveSince: v.interactiveSince !== false,
      at: typeof v.at === 'string' ? v.at : '',
    }
  } catch {
    return null
  }
}

/** True when this page load is the return leg of a redirect sign-in (an interactive sign-in). */
export function isRedirectResponse(loc: { hash: string; search: string }): boolean {
  return /(^|[#?&])(code|error)=/.test(loc.hash) || /(^|[#?&])(code|error)=/.test(loc.search)
}

/**
 * Records P2's evidence ONCE, at the first boot of the W12 build: which MSAL account was already
 * signed in. Exactly one account, and not the return leg of an interactive sign-in, or it records
 * nobody. A record already present is returned untouched: later boots never rewrite it.
 */
export function recordFirstBoot(
  key: string,
  accounts: readonly { homeAccountId: string }[],
  opts: { redirectInFlight: boolean; now?: string },
  kv: FirstBootKv | null = defaultKv(),
): FirstBootRecord | null {
  if (!kv) return null
  const existing = readFirstBoot(key, kv)
  if (existing) return existing
  const only = accounts.length === 1 ? accounts[0] : undefined
  const rec: FirstBootRecord = {
    accountId: only && !opts.redirectInFlight ? only.homeAccountId : null,
    interactiveSince: opts.redirectInFlight,
    at: opts.now ?? new Date().toISOString(),
  }
  try {
    kv.setItem(key, JSON.stringify(rec))
  } catch {
    return null // unrecorded: P2 cannot fire, which is the safe direction
  }
  return rec
}

/** Called before every interactive sign-in. P2 is "no interactive sign-in since", so this ends it. */
export function markInteractiveSignIn(key: string, kv: FirstBootKv | null = defaultKv(), now = new Date().toISOString()): void {
  if (!kv) return
  const rec = readFirstBoot(key, kv) ?? { accountId: null, interactiveSince: true, at: now }
  try {
    kv.setItem(key, JSON.stringify({ ...rec, interactiveSince: true }))
  } catch {
    /* if this cannot be written, the first-boot record cannot have been either */
  }
}

/** `OwnershipEvidence['signedInAtFirstBoot']` for `decideStore`: null unless an account was recorded. */
export function firstBootEvidence(
  key: string,
  kv: FirstBootKv | null = defaultKv(),
): { accountId: string; interactiveSince: boolean } | null {
  const rec = readFirstBoot(key, kv)
  return rec && rec.accountId ? { accountId: rec.accountId, interactiveSince: rec.interactiveSince } : null
}
